import { BinderAmendment, BinderDetails } from "@dune/types";
import {
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	TextArea,
} from "@dune/ui";
import { formatCurrencyPrecise } from "@dune/utils";
import { simpleComparisonToStringMap } from "@dune/vars";
import { formatDate } from "date-fns";

const BinderGeneralInfo = ({
	binderData,
	binderAmendment,
}: {
	binderData: BinderDetails;
	binderAmendment: BinderAmendment;
}) => {
	const { streetNumber, streetName, postalCode, city } =
		binderData.insurer?.society?.headOfficeAddress || {};

	return (
		<div className="flex flex-col gap-4 w-full">
			<h1 className="text-[#144C65] text-2xl font-medium">Généralités</h1>
			<div className="flex flex-col gap-4">
				<div className="flex flex-col border-solid border-1 border-gray-200 rounded-lg p-6 gap-4">
					<div className="flex flex-row gap-6 w-full">
						<Input label="Ref" value={binderData.reference} readOnly />
						<Input label="Avenant" value={binderAmendment.name} readOnly />
					</div>
					<div className="flex flex-row gap-6 w-full">
						<Input
							label="Date d'effet"
							value={
								binderAmendment.effectiveDate
									? formatDate(binderAmendment.effectiveDate, "dd/MM/yyyy")
									: ""
							}
							readOnly
						/>
						<Input
							label="Échéance"
							value={
								binderAmendment.dueDate
									? formatDate(binderAmendment.dueDate, "dd/MM/yyyy")
									: ""
							}
							readOnly
						/>
					</div>
					<div className="flex flex-row gap-6">
						<Input
							label="Date de fin"
							value={
								binderAmendment.endDate
									? formatDate(binderAmendment.endDate, "dd/MM/yyyy")
									: ""
							}
							readOnly
						/>
						<Input
							label="Commission"
							value={binderAmendment.commissionRate ?? ""}
							iconOrText={"%"}
							iconOrTextPosition="right"
							readOnly
						/>
					</div>
					{binderAmendment.constructionCostLimit &&
						binderAmendment.constructionCostLimitComparisonType && (
							<div className="flex flex-row gap-6 items-center grid grid-cols-3">
								<div className="">Montant de la construction</div>
								<div className="flex flex-row gap-6 w-full col-span-2">
									<Select
										defaultValue={
											binderAmendment.constructionCostLimitComparisonType
										}
									>
										<SelectTrigger
											size="none"
											className="disabled:opacity-100 h-11 disabled:bg-gray-100"
											disabled
										>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{Object.entries(simpleComparisonToStringMap).map(
												([key, value]) => {
													return (
														<SelectItem key={key} value={key}>
															{String(value)}
														</SelectItem>
													);
												},
											)}
										</SelectContent>
									</Select>
									<Input
										value={formatCurrencyPrecise(
											binderAmendment.constructionCostLimit,
										)}
										readOnly
									/>
								</div>
							</div>
						)}
				</div>
				<div className="flex flex-col border-solid border-1 border-gray-200 rounded-lg p-6 gap-4">
					<div className="flex flex-row gap-6 w-full">
						<Input
							label="Assureur"
							value={binderData.insurer?.society?.denomination || ""}
							readOnly
						/>
						<Input
							label="Adresse du siège social"
							value={
								streetNumber && streetName && postalCode && city
									? `${streetNumber} ${streetName}, ${postalCode} ${city}`
									: ""
							}
							readOnly
						/>
					</div>
					<div className="flex flex-row gap-6 w-full">
						<Input
							label="Capital social"
							value={formatCurrencyPrecise(binderData.insurer?.capital || 0)}
							readOnly
						/>
						<Input
							label="Numéro d'immatriculation"
							value={binderData.insurer?.registrationNumber || ""}
							readOnly
						/>
					</div>
					<TextArea
						label="Mentions légales"
						value={binderData.insurer?.legalNotices || ""}
						readOnly
					/>
					<div className="grid grid-cols-2 gap-6 w-full">
						{binderData.insurer?.generalConditions
							.filter(
								(generalCondition) =>
									generalCondition.binderType === binderData.binderType,
							)
							.map((generalCondition) => {
								return (
									<Input
										key={generalCondition.id}
										label={`N° conditions générales${generalCondition?.generalConditionsType !== "GENERAL_CONDITIONS" ? ` ${generalCondition.generalConditionsType}` : ""}`}
										value={generalCondition.generalConditionsNumber}
										readOnly
									/>
								);
							})}
					</div>
				</div>
			</div>
		</div>
	);
};

export default BinderGeneralInfo;
